import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { EstadoError } from '@/components/EstadoError';
import { useMarcarLeidas, useNotificaciones, useSinLeer } from './api';

/**
 * La campana y su panel.
 *
 * ## Lo que cambia
 *
 * - **El contador sale de un `COUNT`.** Pedía la primera página y contaba las no
 *   leídas de esas veinte: con veinticinco sin leer decía 20 y no se movía al
 *   leer cinco. Ver
 *   `docs/Cambios/2026-09-28-la-campana-que-se-quedaba-en-20.md`.
 * - **El panel dice si falló.** `catch { list.innerHTML = 'Error cargando
 *   notificaciones' }` — sin motivo y sin forma de reintentar, en un panel que
 *   se cierra y se vuelve a abrir.
 * - **Marcar como leídas no se traga los fallos en silencio.** Se contaban ya,
 *   pero una a una con `.catch()` por notificación; ahora se cuentan juntas y el
 *   panel lo dice si alguna no pudo.
 * - **La campana anuncia cuántas hay.** Era un `<button>` con un icono y un
 *   número dentro de un `<span>`: se leía «botón, 3».
 */
export function Notificaciones() {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  const sinLeer = useSinLeer().data?.unread ?? 0;
  const lista = useNotificaciones(abierto);
  const marcar = useMarcarLeidas();

  // Al abrir, lo que se ve se da por leído. Era el comportamiento del armazón y
  // es lo que limpia el contador; se conserva.
  useEffect(() => {
    if (!abierto || !lista.data?.length) return;
    const pendientes = lista.data.filter((n) => !n.is_read).map((n) => n.notification_id);
    if (pendientes.length) marcar.mutate(pendientes);
    // `marcar` fuera de las dependencias a propósito: es estable y meterlo
    // dispararía el efecto en cada render de la mutación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, lista.data]);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        className="relative flex size-9 items-center justify-center rounded-sm text-5 text-ink-2 hover:bg-sunken hover:text-brand-ink focus-visible:focus-anillo"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-label={
          sinLeer === 0 ? 'Notificaciones, ninguna sin leer'
            : sinLeer === 1 ? 'Notificaciones, una sin leer'
              : `Notificaciones, ${sinLeer} sin leer`
        }
      >
        <i className="bi bi-bell" aria-hidden="true" />
        {sinLeer > 0 && (
          <span
            className="absolute right-0.5 top-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-1 font-bold tabular-nums text-on-brand"
            style={{ background: 'color-mix(in srgb, var(--color-danger) 88%, #000)' }}
            aria-hidden="true"
          >
            {sinLeer > 99 ? '99+' : sinLeer}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 top-full z-40 mt-1 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-sm border border-line bg-surface shadow-raised">
          <p className="flex items-center justify-between border-b border-line bg-sunken px-3 py-2 text-3 font-semibold text-brand-ink">
            Notificaciones
            <button
              type="button"
              className="rounded-xs px-1 text-muted hover:text-ink focus-visible:focus-anillo"
              onClick={() => setAbierto(false)}
              aria-label="Cerrar las notificaciones"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </p>

          <div className="max-h-96 overflow-y-auto">
            {lista.isPending ? (
              <p className="p-4 text-center text-2 text-muted">Cargando…</p>
            ) : lista.isError ? (
              <div className="p-3">
                <EstadoError
                  err={lista.error}
                  que="tus notificaciones"
                  onReintentar={() => void lista.refetch()}
                />
              </div>
            ) : !lista.data?.length ? (
              <p className="p-6 text-center text-3 text-muted">Sin notificaciones</p>
            ) : (
              <ul>
                {lista.data.map((n) => (
                  <li
                    key={n.notification_id}
                    className={cn(
                      'border-b border-line px-3 py-2.5 last:border-b-0',
                      !n.is_read && 'bg-sunken',
                    )}
                  >
                    <p className={cn('text-3 text-ink', !n.is_read && 'font-bold')}>{n.title}</p>
                    <p className="mt-0.5 text-2 text-muted">{n.message}</p>
                    <p className="mt-1 text-1 text-muted">{Utils.relativeTime(n.created_at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {marcar.data?.fallaron ? (
            <p className="border-t border-line px-3 py-2 text-2 text-warn-ink" role="status">
              {marcar.data.fallaron === 1
                ? 'Una no se pudo marcar como leída y volverá a salir.'
                : `${marcar.data.fallaron} no se pudieron marcar como leídas y volverán a salir.`}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
